import { useState } from "react";

import { useToast } from "@/app/providers/ToastProvider";
import { CurrencyInput } from "@/components/common/CurrencyInput";
import { FormErrorSummary } from "@/components/common/FormErrorSummary";
import { FormField } from "@/components/common/FormField";
import { LoadingOverlay } from "@/components/common/LoadingOverlay";
import { NumericStepper } from "@/components/common/NumericStepper";
import { unitLabel, UnitPicker } from "@/components/common/UnitPicker";
import { BackButton } from "@/components/ui/back-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NAFA_FORM_WIDTH } from "@/components/ui/page-container";
import { PageHeader } from "@/components/ui/page-header";
import { useCurrency } from "@/features/accounts/hooks/useCurrency";
import { FALLBACK_CATEGORY_CODE } from "@/features/categories/api/categoriesApi";
import { useCreateProduct, useUpdateProduct } from "@/features/products/hooks/useProducts";
import type { Product } from "@/features/products/types";
import { useProvisions } from "@/features/provisions/hooks/useProvisions";
import {
  emptyIngredientLine,
  filledIngredients,
  IngredientLines,
  type IngredientLine,
} from "@/features/recipes/components/IngredientLines";
import { useCreateRecipe } from "@/features/recipes/hooks/useRecipes";
import { getApiValidationErrors } from "@/lib/apiClient";
import { parseLocalizedNumber } from "@/lib/format";
import { focusFirstField } from "@/lib/forms";
import { toErrorMessage } from "@/lib/swr";

type ProductFieldErrors = {
  name?: string;
  precision?: string;
  price?: string;
};

const PRODUCT_FIELD_IDS = {
  name: "product-name",
  category: "product-category",
  description: "product-description",
  precision: "product-precision",
  price: "product-price",
} as const;

export default function ProductFormPage({ product, onBack }: { product?: Product; onBack: () => void }) {
  const isEditing = Boolean(product);

  const [typeCode] = useState(product?.typeCode ?? FALLBACK_CATEGORY_CODE);
  const [name, setName] = useState(product?.name ?? "");
  const [description, setDescription] = useState(product?.description ?? "");
  const [unit, setUnit] = useState(product?.measurementUnit ?? "u");
  const [precision, setPrecision] = useState(String(product?.measurementPrecision ?? "1"));
  const [price, setPrice] = useState(product ? String(product.price) : "");
  const [fieldErrors, setFieldErrors] = useState<ProductFieldErrors>({});
  const [formError, setFormError] = useState<string>();

  /** A product that is cooked needs a recipe; asking later means a second trip. */
  const [withRecipe, setWithRecipe] = useState(false);
  const [recipeLines, setRecipeLines] = useState<IngredientLine[]>([emptyIngredientLine()]);

  const { currency } = useCurrency();
  const { create, isCreating } = useCreateProduct();
  const { update, isUpdating } = useUpdateProduct();
  const { create: createRecipe, isCreating: isCreatingRecipe } = useCreateRecipe();
  const { provisions } = useProvisions();
  const toast = useToast();

  const isSaving = isCreating || isUpdating || isCreatingRecipe;

  const clearFieldError = (field: keyof ProductFieldErrors) => {
    setFieldErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));
    setFormError(undefined);
  };

  const validate = () => {
    const errors: ProductFieldErrors = {};
    if (!name.trim()) errors.name = "Donnez un nom au produit.";
    if (!(parseFloat(precision) > 0)) errors.precision = "Indiquez une quantité supérieure à 0.";
    if (!(parseLocalizedNumber(price) > 0)) errors.price = "Indiquez un prix de vente.";
    setFieldErrors(errors);
    setFormError(Object.keys(errors).length > 0 ? "Corrigez les champs signalés avant d’enregistrer." : undefined);
    focusFirstField(Object.keys(errors).map((field) => PRODUCT_FIELD_IDS[field as keyof typeof PRODUCT_FIELD_IDS]));
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(undefined);
    if (!validate()) return;

    const payload = {
      name: name.trim(),
      description: description.trim() || undefined,
      measurementUnit: unit,
      measurementPrecision: parseFloat(precision),
      price: parseLocalizedNumber(price),
      currency: product?.currency ?? currency,
      typeCode,
    };

    if (product) {
      try {
        await update(product.id, payload);
        toast.success(`« ${payload.name} » mis à jour.`);
        onBack();
      } catch (e) {
        // The form keeps its values so nothing is lost.
        const apiErrors = getApiValidationErrors(e);
        const nextErrors: ProductFieldErrors = {
          name: apiErrors.name,
          precision: apiErrors.measurementPrecision,
          price: apiErrors.price,
        };
        const fieldErrorIds = Object.entries(nextErrors)
          .filter(([, message]) => Boolean(message))
          .map(([field]) => PRODUCT_FIELD_IDS[field as keyof typeof PRODUCT_FIELD_IDS]);
        setFieldErrors(nextErrors);
        setFormError(
          fieldErrorIds.length > 0
            ? "Corrigez les champs signalés avant d’enregistrer."
            : (toErrorMessage(e) ?? undefined),
        );
        focusFirstField(fieldErrorIds);
        toast.error(toErrorMessage(e) ?? "Le produit n’a pas pu être enregistré.");
      }
      return;
    }

    const ingredients = withRecipe ? filledIngredients(recipeLines) : [];
    if (withRecipe && ingredients.length === 0) {
      toast.error("Ajoutez au moins une matière première à la recette, ou décochez-la.");
      return;
    }

    let created;
    try {
      created = await create(payload);
    } catch (e) {
      const apiErrors = getApiValidationErrors(e);
      const nextErrors: ProductFieldErrors = {
        name: apiErrors.name,
        precision: apiErrors.measurementPrecision,
        price: apiErrors.price,
      };
      const fieldErrorIds = Object.entries(nextErrors)
        .filter(([, message]) => Boolean(message))
        .map(([field]) => PRODUCT_FIELD_IDS[field as keyof typeof PRODUCT_FIELD_IDS]);
      setFieldErrors(nextErrors);
      setFormError(
        fieldErrorIds.length > 0
          ? "Corrigez les champs signalés avant d’enregistrer."
          : (toErrorMessage(e) ?? undefined),
      );
      focusFirstField(fieldErrorIds);
      toast.error(toErrorMessage(e) ?? "Le produit n’a pas pu être créé.");
      return;
    }

    if (!withRecipe) {
      toast.success(`« ${payload.name} » ajouté au catalogue.`);
      onBack();
      return;
    }

    // The product exists from here on, so a failing recipe must say so rather
    // than read as if nothing had been saved.
    try {
      await createRecipe({
        name: `${payload.name} — recette`,
        productId: created.id,
        ingredients,
      });
      toast.success(`« ${payload.name} » ajouté au catalogue, avec sa recette.`);
      onBack();
    } catch (e) {
      toast.error(
        `Le produit « ${payload.name} » est créé, mais sa recette a échoué : ${
          toErrorMessage(e) ?? "erreur inconnue"
        }. Ajoutez-la depuis Recettes.`,
      );
      onBack();
    }
  };

  return (
    <form onSubmit={handleSubmit} className={`flex ${NAFA_FORM_WIDTH} flex-col gap-6`}>
      {isSaving && <LoadingOverlay message="Enregistrement…" />}

      <div className="flex items-center gap-3">
        <BackButton onClick={onBack} />
        <PageHeader
          title={isEditing ? "Modifier le produit" : "Nouveau produit"}
          subtitle={isEditing ? "Le nouveau prix vaut pour les prochaines ventes." : "Cinq informations suffisent."}
        />
      </div>

      <FormErrorSummary
        fields={[
          { id: PRODUCT_FIELD_IDS.name, label: "Nom", message: fieldErrors.name },
          { id: PRODUCT_FIELD_IDS.precision, label: "Plus petite quantité vendue", message: fieldErrors.precision },
          { id: PRODUCT_FIELD_IDS.price, label: "Prix de vente", message: fieldErrors.price },
        ]}
      >
        {formError}
      </FormErrorSummary>

      <FormField
        label="Nom"
        htmlFor={PRODUCT_FIELD_IDS.name}
        error={fieldErrors.name}
        errorId={`${PRODUCT_FIELD_IDS.name}-error`}
      >
        <Input
          id={PRODUCT_FIELD_IDS.name}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            clearFieldError("name");
          }}
          placeholder="ex. Poulet yassa"
          autoFocus
          aria-invalid={Boolean(fieldErrors.name)}
          aria-describedby={fieldErrors.name ? `${PRODUCT_FIELD_IDS.name}-error` : undefined}
        />
      </FormField>

      <fieldset className="min-w-0">
        <legend className="mb-2 text-[15px] font-medium">Unité de mesure</legend>
        <UnitPicker value={unit} onChange={setUnit} />
      </fieldset>

      <FormField
        label="Plus petite quantité vendue"
        hint={"Vous vendrez ce produit par tranches de " + (precision || "…") + " " + unitLabel(unit) + "."}
        error={fieldErrors.precision}
        htmlFor={PRODUCT_FIELD_IDS.precision}
        errorId={`${PRODUCT_FIELD_IDS.precision}-error`}
        hintId={`${PRODUCT_FIELD_IDS.precision}-hint`}
      >
        <NumericStepper
          id={PRODUCT_FIELD_IDS.precision}
          step={0.01}
          min={0}
          value={precision}
          onChange={(e) => {
            setPrecision(String(e));
            clearFieldError("precision");
          }}
          aria-invalid={Boolean(fieldErrors.precision)}
          aria-describedby={
            fieldErrors.precision
              ? `${PRODUCT_FIELD_IDS.precision}-error ${PRODUCT_FIELD_IDS.precision}-hint`
              : `${PRODUCT_FIELD_IDS.precision}-hint`
          }
        />
      </FormField>

      <FormField
        label="Prix de vente"
        error={fieldErrors.price}
        htmlFor={PRODUCT_FIELD_IDS.price}
        errorId={`${PRODUCT_FIELD_IDS.price}-error`}
      >
        <CurrencyInput
          id={PRODUCT_FIELD_IDS.price}
          value={price}
          onValueChange={(value) => {
            setPrice(value);
            clearFieldError("price");
          }}
          currency={product?.currency ?? currency}
          className="font-semibold"
          aria-invalid={Boolean(fieldErrors.price)}
          aria-describedby={fieldErrors.price ? `${PRODUCT_FIELD_IDS.price}-error` : undefined}
        />
      </FormField>

      <FormField
        label="Description"
        htmlFor={PRODUCT_FIELD_IDS.description}
        hint="Facultatif."
        hintId={`${PRODUCT_FIELD_IDS.description}-hint`}
      >
        <Input
          id={PRODUCT_FIELD_IDS.description}
          aria-describedby={`${PRODUCT_FIELD_IDS.description}-hint`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Précision utile pour vos équipes"
        />
      </FormField>

      {!isEditing && (
        <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4">
          <label className="flex items-start gap-3" htmlFor="withRecipe">
            <input
              id="withRecipe"
              type="checkbox"
              checked={withRecipe}
              onChange={(e) => setWithRecipe(e.target.checked)}
              className="mt-0.5 size-5 shrink-0 accent-brand"
            />
            <span>
              <span className="text-[15px] font-medium">Définir sa recette maintenant</span>
              <span className="block text-sm text-muted-foreground">
                Ce qu’une unité de ce produit consomme. Sans recette, il ne pourra pas être produit depuis le stock
                provisionnel.
              </span>
            </span>
          </label>

          {withRecipe ? (
            provisions.length > 0 ? (
              <IngredientLines lines={recipeLines} onChange={setRecipeLines} provisions={provisions} />
            ) : (
              <p className="rounded-lg border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning-foreground">
                Créez d’abord des matières premières : une recette a besoin d’au moins l’une d’elles.
              </p>
            )
          ) : null}
        </div>
      )}

      {isEditing && (
        <p className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          La recette de ce produit se modifie depuis l’écran Recettes.
        </p>
      )}

      <div className="flex gap-3">
        <Button type="submit" size="lg" disabled={isSaving}>
          {isEditing ? "Enregistrer" : withRecipe ? "Ajouter le produit et sa recette" : "Ajouter le produit"}
        </Button>
        <Button type="button" variant="outline" size="lg" onClick={onBack}>
          Annuler
        </Button>
      </div>
    </form>
  );
}
